/**
 * Pure calculation functions for the CashFlow Statement module.
 */

import type { MonthlySummary, Asset, Liability, DerivedMetrics } from '../types/cashflow';

export function deriveMetrics(
  summary: MonthlySummary,
  assets: Asset[],
  liabilities: Liability[],
): DerivedMetrics {
  const totalIncome = summary.earned_income + summary.passive_income + summary.portfolio_income;
  const totalExpenses =
    summary.tax_expenses +
    summary.housing_expenses +
    summary.living_expenses +
    summary.debt_expenses +
    summary.other_expenses;
  const monthlyCashflow = totalIncome - totalExpenses;
  const passiveIncomeRatio = totalIncome > 0 ? summary.passive_income / totalIncome : 0;
  const financialFreedomPct = totalExpenses > 0 ? summary.passive_income / totalExpenses : 0;

  const monthAssets = assets.filter((a) => a.month === summary.month);
  const monthLiabilities = liabilities.filter((l) => l.month === summary.month);
  const totalAssets = monthAssets.reduce((sum, a) => sum + a.current_value, 0);
  const totalLiabilities = monthLiabilities.reduce((sum, l) => sum + l.outstanding_balance, 0);
  const netWorth = totalAssets - totalLiabilities;

  return {
    month: summary.month,
    totalIncome,
    totalExpenses,
    monthlyCashflow,
    passiveIncomeRatio,
    financialFreedomPct,
    totalAssets,
    totalLiabilities,
    netWorth,
  };
}

export function deriveAllMetrics(
  summaries: MonthlySummary[],
  assets: Asset[],
  liabilities: Liability[],
): DerivedMetrics[] {
  return summaries.map((s) => deriveMetrics(s, assets, liabilities));
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(parseInt(year), parseInt(m) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export function cashflowColor(value: number): string {
  if (value > 0) return 'var(--color-success)';
  if (value === 0) return 'var(--color-warning)';
  return 'var(--color-error)';
}

export function cashflowBannerColor(value: number): string {
  if (value > 0) return '#22543d'; // dark green bg
  if (value === 0) return '#744210'; // dark amber
  return '#742a2a'; // dark red
}
